import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import VehicleCard from "@/components/feature/VehicleCard";
import { useVehicles } from "@/hooks/useVehicles";
import HeroSection from "./components/HeroSection";
import CategoryStrip from "./components/CategoryStrip";
import HowItWorks from "./components/HowItWorks";
import TrustSection from "./components/TrustSection";
import ReviewsStrip from "./components/ReviewsStrip";
import HostCta from "./components/HostCta";

export default function Home() {
  const location = useLocation();
  const { vehicles, loading, error, reload } = useVehicles({ limit: 6 });

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const target = document.getElementById(id);
    if (target) {
      window.requestAnimationFrame(() => target.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-background-50">
      <Navbar />

      <HeroSection />

      <section className="mx-auto w-full max-w-7xl px-4 pt-4 md:px-8">
        <Reveal>
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">Browse by type</span>
              <h2 className="mt-3 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                What are you driving?
              </h2>
            </div>
            <Link
              to="/browse"
              className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-500 transition-colors hover:text-primary-400"
            >
              View all cars <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </Reveal>
        <Reveal className="mt-8" delay={80}>
          <CategoryStrip />
        </Reveal>
      </section>

      <section id="featured" className="mx-auto w-full max-w-7xl px-4 py-16 md:px-8 md:py-24">
        <Reveal className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">Featured fleet</span>
            <h2 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
              Popular cars on Gari right now
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              Handpicked, highly-rated vehicles from verified hosts across Kenya.
            </p>
          </div>
          <Link
            to="/browse"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-5 py-3 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
          >
            <i className="ri-grid-line"></i> See the full fleet
          </Link>
        </Reveal>

        {error && (
          <div className="mt-10 flex flex-col items-start gap-3 rounded-xl border border-secondary-700/40 bg-secondary-950/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center gap-2 text-sm text-secondary-300">
              <i className="ri-error-warning-line"></i> {error}
            </p>
            <button
              type="button"
              onClick={reload}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50"
            >
              <i className="ri-refresh-line"></i> Try again
            </button>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {loading &&
            !error &&
            Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="overflow-hidden rounded-2xl border border-background-200/70 bg-background-100">
                <div className="h-52 w-full animate-pulse bg-background-200/60" />
                <div className="space-y-3 p-4">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-background-200/60" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-background-200/60" />
                  <div className="h-3 w-1/3 animate-pulse rounded bg-background-200/60" />
                </div>
              </div>
            ))}

          {!loading &&
            !error &&
            vehicles.map((vehicle, index) => (
              <Reveal key={vehicle.id} delay={(index % 3) * 100}>
                <VehicleCard vehicle={vehicle} />
              </Reveal>
            ))}
        </div>
      </section>

      <TrustSection />
      <HowItWorks />
      <ReviewsStrip />
      <HostCta />

      <Footer />
    </div>
  );
}